<template xmlns="http://www.w3.org/1999/html">
  <BasePage :pageTitle="rep?.Name || __DICT__.loading" show-menu show-footer back-button>
    <Card v-if="rep" margin="">
      <RepProfile :rep-info="rep" :use-action-sheet="true" @phone-click="handlePhoneClick"/>
      <div class="p-4">
        <!-- Rooms List -->
        <AccommodationList
            title="Rooms"
            :items="rooms.rooms"
            type="rooms"
        />

        <!-- Inspections List -->
        <AccommodationList
            title="Inspections"
            :items="inspections.inspections"
            type="inspections"
        />

        <PaxAllocation accordion v-if="liftPasses.length > 0" title="Lift Passes" class="mt-2" show-count :inhabitants=liftPasses></PaxAllocation>
      </div>
    </Card>

    <!-- Loading State -->
    <LoadingSpinner v-else-if="isLoading" color="blue" :text="__DICT__.loading" centered />

    <!-- Error State -->
    <div v-else class="empty">
      <div class="empty-icon">
        <IconMoodSad class="icon" :size="24" />
      </div>
      <p class="empty-title">{{ __DICT__.repNotFound }}</p>
      <p class="empty-subtitle text-muted">
        {{ __DICT__.repNotFoundMessage }}
      </p>
      <div class="empty-action">
        <button class="btn btn-primary" @click="router.push('/reps')">
          {{ __DICT__.backToRepList }}
        </button>
      </div>
    </div>

    <!-- Phone Action Sheet -->
    <PhoneNumberActionSheet
        :phone-number="selectedPhoneNumber"
        :is-visible="showActionSheet"
        @close="showActionSheet = false"
    />
  </BasePage>
</template>

<script setup>
import {ref, onMounted} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useWeekStore} from '@/stores/week.js'
import {usePermissions} from '@/composables/usePermissions'
import BasePage from "@/components/BasePage.vue"
import PhoneNumberActionSheet from "@/components/PhoneNumberActionSheet.vue"
import RepProfile from "@/components/RepProfile.vue"
import AccommodationList from "@/components/AccommodationList.vue"
import { processAccommodationData, getRepColor } from "@/utils/accommodationUtils.js"
import PaxAllocation from "@/components/PaxAllocation.vue"
import { Card, LoadingSpinner } from "@/components/atomic"
import { IconMoodSad } from '@tabler/icons-vue'

const route = useRoute()
const router = useRouter()
const weekStore = useWeekStore()
const { filter } = usePermissions()
const isLoading = ref(true)
const rep = ref(null)
const liftPasses = ref([])
const rooms = ref({
  pax: 0,
  roomCount: 0,
  rooms: []
})
const inspections = ref({
  pax: 0,
  inspectionCount: 0,
  inspections: []
})
const selectedPhoneNumber = ref('')
const showActionSheet = ref(false)

const handlePhoneClick = (number) => {
  selectedPhoneNumber.value = number
  showActionSheet.value = true
}

async function loadRepData() {
  isLoading.value = true
  try {
    const repData = await weekStore.repData
    const repVal = repData.find(r => String(r['GBS ID']) === String(route.params.repId))

    // Check if user can view this rep using permission filter
    const canView = repVal && filter([repVal], 'viewRep').length > 0
    if (canView) {
      rep.value = repVal
    } else {
      rep.value = "ERROR"
      return
    }

    const repId = parseInt(rep.value['GBS ID'])
    const weekData = weekStore.weekData

    liftPasses.value = weekData.filter(passenger => {
      if (!passenger.allocPlaces || !Array.isArray(passenger.allocPlaces)) return false
      return passenger.allocPlaces.some(alloc => {
        const liftpass = alloc.liftpass
        if (!liftpass) return false
        const retVal = liftpass.staffMembers?.some(staff => staff.id === parseInt(route.params.repId))
        return retVal
      })
    })
    // Use the utility function to process accommodation data
    const processedData = processAccommodationData(weekData, repId)
    rooms.value = processedData.rooms
    inspections.value = processedData.inspections

  } catch (error) {
    console.error('Error loading rep data:', error)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadRepData()
})
</script>